import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import {
  GenericTableAction,
  GenericTableColumn,
  TableSortDirection,
} from '@shared/models/generic-table.model';
import { SettingsContextService } from '@core/services/settings-context.service';

interface TableBadgeValue {
  badge: true;
  text: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'success';
  style?: { [key: string]: string };
}

@Component({
  selector: 'app-generic-table',
  templateUrl: './generic-table.component.html',
  styleUrl: './generic-table.component.css',
  standalone: false,
})
export class GenericTableComponent<T = unknown> implements OnChanges {

  public readonly settingsContext = inject(SettingsContextService);

  @Input() columns: GenericTableColumn[] = [];
  @Input() data: T[] = [];
  @Input() loading = false;
  @Input() emptyMessage = 'No data found.';
  @Input() isPaginated = true;
  @Input() pageSizeOptions: number[] = [10, 50, 100, 500];
  @Input() actions: GenericTableAction[] = [];

  sortColumn: string | null = null;
  sortDirection: TableSortDirection = 'asc';

  page = 1;

  pageSize = Number(
    this.settingsContext.gridSettings.itemsPerPage
  );

  Math = Math;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['columns']) {
      this.page = 1;

      if (this.page > this.totalPages) {
        this.page = Math.max(1, this.totalPages);
      }
    }
  }

  get visibleColumns(): GenericTableColumn[] {
    return [...this.columns]
      .filter(column => column.visible !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  get displayedColumns(): string[] {

    const ids = this.visibleColumns.map(col => col.id);

    if (this.actions.length > 0) {
      ids.push('__actions');
    }

    return ids;
  }

  get sortedData(): T[] {

    if (!this.sortColumn) {
      return [...this.data];
    }

    const activeColumn = this.visibleColumns.find(
      col => col.id === this.sortColumn
    );

    if (!activeColumn) {
      return [...this.data];
    }

    const sorted = [...this.data].sort((a, b) => {

      const aValue = this.getCellValue(a, activeColumn);
      const bValue = this.getCellValue(b, activeColumn);

      if (aValue == null && bValue == null) {
        return 0;
      }

      if (aValue == null) {
        return 1;
      }

      if (bValue == null) {
        return -1;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return this.sortDirection === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();

      return this.sortDirection === 'asc'
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);

    });

    return sorted;
  }

  get totalPages(): number {

    if (!this.isPaginated || this.sortedData.length === 0) {
      return 1;
    }

    return Math.max(
      1,
      Math.ceil(this.sortedData.length / this.pageSize)
    );
  }

  get paginatedData(): T[] {

    if (!this.isPaginated) {
      return this.sortedData;
    }

    const start = (this.page - 1) * this.pageSize;

    return this.sortedData.slice(
      start,
      start + this.pageSize
    );
  }

  trackColumn(_: number, column: GenericTableColumn): string {
    return column.id;
  }

  handleSort(column: GenericTableColumn): void {

    if (
      !this.settingsContext.gridSettings.enableSorting ||
      !column.sortable
    ) {
      return;
    }

    if (this.sortColumn === column.id) {

      this.sortDirection =
        this.sortDirection === 'asc'
          ? 'desc'
          : 'asc';

      return;
    }

    this.sortColumn = column.id;
    this.sortDirection = 'asc';
  }

  getCellValue(row: any, column: GenericTableColumn): unknown {

    if (column.value) {
      return column.value(row);
    }

    return row?.[column.id] ?? '-';
  }

  getCellLines(row: any, column: GenericTableColumn): string[] {

    const value = this.getCellValue(row, column);

    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => String(item ?? '-'));
  }

  isMultiLineCell(
    row: any,
    column: GenericTableColumn
  ): boolean {

    return this.getCellLines(row, column).length > 0;
  }

  isBadgeValue(value: unknown): value is TableBadgeValue {

    return (
      !!value &&
      typeof value === 'object' &&
      (value as { badge?: boolean }).badge === true
    );
  }

  getBadgeValue(
    row: any,
    column: GenericTableColumn
  ): TableBadgeValue | null {

    const value = this.getCellValue(row, column);

    return this.isBadgeValue(value)
      ? value
      : null;
  }

  getBadgeValues(
    row: any,
    column: GenericTableColumn
  ): TableBadgeValue[] {

    const value = this.getCellValue(row, column);

    if (Array.isArray(value)) {
      return value.filter(v => this.isBadgeValue(v)) as TableBadgeValue[];
    }

    return this.isBadgeValue(value) ? [value] : [];
  }

  resolveAlignClass(column: GenericTableColumn): string {

    if (column.align === 'center') {
      return 'cell-center';
    }

    if (column.align === 'right') {
      return 'cell-right';
    }

    return 'cell-left';
  }

  previousPage(): void {
    this.page = Math.max(1, this.page - 1);
  }

  nextPage(): void {
    this.page = Math.min(
      this.totalPages,
      this.page + 1
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = Number(size);
    this.page = 1;
    this.settingsContext.updateGridSettings({
    itemsPerPage: String(size)
    });
  }

  isActionHidden(
    action: GenericTableAction,
    row: any
  ): boolean {

    if (typeof action.hidden === 'function') {
      return action.hidden(row);
    }

    return !!action.hidden;
  }

  isActionDisabled(
    action: GenericTableAction,
    row: any
  ): boolean {

    if (typeof action.disabled === 'function') {
      return action.disabled(row);
    }

    return !!action.disabled;
  }

  getActionLabel(
    action: GenericTableAction,
    row: any
  ): string {

    if (typeof action.label === 'function') {
      return action.label(row);
    }

    return action.label;
  }

  copyValue(value: unknown, event: Event): void {

    event.stopPropagation();

    if (value == null || value === '-') {
      return;
    }

    void navigator.clipboard?.writeText(
      String(value)
    );
  }
}